import { TFunction } from 'i18next';
import * as yup from 'yup';
import { GeneralStatusEnum } from '../../../common/enums/common.enum';
import { BillingItemTypeEnum } from '../../../common/enums/invoicing.enum';
import { BillingItemDto } from '../../../common/interfaces/invoicing.interface';
import { toMoneyInputValue } from '../../../utilities/helper/money.helper';
import { decimalText, maxText, moneyText, nameText } from './rules';

export interface BillingItemFormValues {
  name: string;
  /** Empty means "the same as the name": it follows the name until it is typed in. */
  pdfLabel: string;
  type: BillingItemTypeEnum;
  unitPriceNet: string;
  /** null until picked; a new item in the VAT system starts at the general rate. */
  vatRate: number | null;
  unit: string;
  defaultQuantity: string;
  status: GeneralStatusEnum;
}

export const BILLING_ITEM_FIELDS: (keyof BillingItemFormValues)[] = ['name', 'pdfLabel', 'type', 'unitPriceNet', 'vatRate', 'unit', 'defaultQuantity', 'status'];

export const VAT_RATES = [20, 10, 0];
export const DEFAULT_VAT_RATE = 20;

export const toItemFormValues = (item: BillingItemDto | null | undefined, vatApplicable: boolean): BillingItemFormValues => ({
  name: item?.name ?? '',
  // a label equal to the name is "following the name", so it keeps following it
  pdfLabel: item && item.pdfLabel !== item.name ? item.pdfLabel : '',
  type: item?.type ?? BillingItemTypeEnum.FIXED_QUANTITY,
  unitPriceNet: item ? toMoneyInputValue(item.unitPriceNet) : '',
  vatRate: item ? item.vatRate : vatApplicable ? DEFAULT_VAT_RATE : 0,
  unit: item?.unit ?? '',
  defaultQuantity: String(item?.defaultQuantity ?? 1),
  status: item?.status ?? GeneralStatusEnum.ACTIVE
});

/** Bounds from UpsertBillingItemDto. */
export const billingItemSchema = (t: TFunction, options: { vatApplicable: boolean }) =>
  yup.object({
    name: nameText(t, 2, 120, t('validation.item.nameEmpty')),
    pdfLabel: nameText(t, 2, 200),
    type: yup.mixed<BillingItemTypeEnum>().oneOf(Object.values(BillingItemTypeEnum)),
    unitPriceNet: moneyText(t, { required: t('validation.item.priceEmpty') }),
    vatRate: options.vatApplicable
      ? yup.number().nullable().default(null).test('picked', t('validation.item.vatRate'), value => value !== null && value !== undefined)
      : yup.number().nullable(),
    unit: maxText(t, 20),
    defaultQuantity: decimalText(t, { decimals: 2, min: 0 }),
    status: yup.mixed<GeneralStatusEnum>()
  });
